import React, { useState } from 'react';
import { Checkbox, TextInput, Textarea } from '@mantine/core';
import ReactSelect from 'react-select';
import styles from './MeetingScheduleForm.module.scss';
import { ReactComponent as AlarmIcon } from "assets/alarm.svg";
import { ReactComponent as BlueAlarmIcon } from "assets/blueAlarm.svg";
import { ReactComponent as CloseIcon } from "assets/close.svg";
import StartTimeModal from 'Modules/MapRouter/modules/CurrentRoute/components/StartTimeModal';
import DurationModal from 'Modules/MapRouter/modules/CurrentRoute/components/DurationModal';

interface MeetingScheduleFormProps {
  startTime: string;
  onStartTimeChange: (time: string) => void;
  duration: string;
  onDurationChange: (duration: string) => void;
  location?: string;
  onLocationChange?: (location: string) => void;
  agenda?: string;
  onAgendaChange?: (agenda: string) => void;
  addToCalendar?: boolean;
  onAddToCalendarChange?: (checked: boolean) => void;
  sendToClient?: boolean;
  onSendToClientChange?: (checked: boolean) => void;
  customerName?: string;
  customerEmail?: string;
  showCalendarOptions?: boolean; // Show "Add to calendar" checkbox
  hideLocation?: boolean; // Hide location field (when redundant)
  hideCheckbox?: boolean; // Hide the "Add to my calendar" checkbox (when already in calendar context)
}

const MeetingScheduleForm: React.FC<MeetingScheduleFormProps> = ({
  startTime,
  onStartTimeChange,
  duration,
  onDurationChange,
  location = '',
  onLocationChange,
  agenda = '',
  onAgendaChange,
  addToCalendar = false,
  onAddToCalendarChange,
  sendToClient = false,
  onSendToClientChange,
  customerName,
  customerEmail,
  showCalendarOptions = true,
  hideLocation = false,
  hideCheckbox = false,
}) => {
  const [isStartTimeModalOpen, setIsStartTimeModalOpen] = useState(false);
  const [isDurationModalOpen, setIsDurationModalOpen] = useState(false);

  return (
    <div className={styles.formContainer}>
      {/* Start Time */}
      <div className={styles.timeWrapper}>
        <div className={styles.timeHeader}>
          <span className={styles.label}>Start Time</span>
          <span className={styles.helperText}>
            If you need to be at the location at this time
          </span>
        </div>
        <div>
          {startTime ? (
            <div className={styles.time}>
              <BlueAlarmIcon width={35} height={35} />
              <span
                onClick={() => setIsStartTimeModalOpen(true)}
                className={styles.timeValue}
              >
                {startTime}
              </span>
              <CloseIcon
                className={styles.closeIcon}
                color="#4F7D9C"
                width={20}
                height={20}
                onClick={() => onStartTimeChange('')}
              />
            </div>
          ) : (
            <div
              onClick={() => setIsStartTimeModalOpen(true)}
              className={styles.timeComponent}
            >
              <AlarmIcon />
              <span className={styles.noTimeText}>No time set</span>
            </div>
          )}
          <StartTimeModal
            isOpen={isStartTimeModalOpen}
            onClose={() => setIsStartTimeModalOpen(false)}
            startTime={startTime}
            setStartTime={onStartTimeChange}
            setIsStartTimeModalOpen={setIsStartTimeModalOpen}
          />
        </div>
      </div>

      {/* Meeting Duration */}
      <div className={styles.durationWrapper}>
        <label className={styles.label}>Meeting Duration</label>
        <div
          onClick={() => setIsDurationModalOpen(true)}
          className={styles.durationDisplay}
        >
          <span className={styles.durationValue}>
            {duration.includes('min')
              ? duration.split(' ')[0]
              : Number(duration.split(' ')[0]).toString()}
          </span>
          <span className={styles.durationUnit}>
            {duration.includes('min') ? 'Minutes' : parseFloat(duration) === 1.0 ? 'Hour' : 'Hours'}
          </span>
        </div>
        <DurationModal
          isOpen={isDurationModalOpen}
          onClose={() => setIsDurationModalOpen(false)}
          duration={duration}
          setDuration={onDurationChange}
          setIsDurationModalOpen={setIsDurationModalOpen}
        />
      </div>

      {/* Calendar Integration Options */}
      {showCalendarOptions && (
        <div className={styles.calendarSection}>
          {!hideCheckbox && (
            <Checkbox
              checked={addToCalendar}
              onChange={(e) => onAddToCalendarChange?.(e.currentTarget.checked)}
              label="Add to my calendar"
              styles={{
                root: {
                  marginBottom: 12,
                },
                body: {
                  alignItems: 'center',
                },
                label: {
                  fontSize: 16,
                  cursor: 'pointer',
                }
              }}
            />
          )}

          {(addToCalendar || hideCheckbox) && (
            <>
              {/* Location Field (only show if not hidden) */}
              {!hideLocation && (
                <div className={styles.durationWrapper}>
                  <label style={{ fontSize: 12, color: '#000' }}>Location (Optional)</label>
                  <div style={{ padding: '12px 0' }}>
                    <TextInput
                      value={location}
                      onChange={(e) => onLocationChange?.(e.currentTarget.value)}
                      placeholder="Meeting location"
                      styles={{
                        root: {
                          border: 'none',
                          width: '100%',
                        },
                        wrapper: {
                          border: 'none',
                        },
                        input: {
                          fontSize: 16,
                          border: 'none',
                          borderBottom: 'none',
                          borderRadius: 4,
                          padding: '10px 16px',
                          margin: 0,
                          backgroundColor: '#F5F5F5',
                          minHeight: '30px',
                          boxShadow: 'none',
                          boxSizing: 'border-box',
                          '&:focus': {
                            outline: 'none',
                            border: 'none',
                            borderBottom: 'none',
                            boxShadow: 'none',
                            backgroundColor: '#F5F5F5',
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Agenda Field */}
              <div className={styles.durationWrapper}>
                <label style={{ fontSize: 12, color: '#000' }}>Agenda (Optional)</label>
                <div style={{ padding: '12px 0' }}>
                  <Textarea
                    value={agenda}
                    onChange={(e) => onAgendaChange?.(e.currentTarget.value)}
                    placeholder="What will you discuss?"
                    rows={5}
                    styles={{
                      root: {
                        border: 'none',
                        width: '100%',
                      },
                      wrapper: {
                        border: 'none',
                      },
                      input: {
                        fontSize: 16,
                        border: 'none',
                        borderBottom: 'none',
                        borderRadius: 4,
                        padding: '10px 16px',
                        margin: 0,
                        backgroundColor: '#F5F5F5',
                        minHeight: 100,
                        resize: 'vertical',
                        boxShadow: 'none',
                        boxSizing: 'border-box',
                        '&:focus': {
                          outline: 'none',
                          border: 'none',
                          borderBottom: 'none',
                          boxShadow: 'none',
                          backgroundColor: '#F5F5F5',
                        }
                      }
                    }}
                  />
                </div>
              </div>

              {/* Send to Client Option */}
              {customerName && customerEmail && (
                <div className={styles.sendToClientSection}>
                  <Checkbox
                    checked={sendToClient}
                    onChange={(e) => onSendToClientChange?.(e.currentTarget.checked)}
                    label={`Send invite to ${customerName}`}
                    styles={{
                      body: {
                        alignItems: 'center',
                      },
                      label: {
                        fontSize: 16,
                        cursor: 'pointer',
                      }
                    }}
                  />
                  {!customerEmail && (
                    <div className={styles.warningMessage}>
                      No email address available for {customerName}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MeetingScheduleForm;
